import React from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
    AnalystOption,
    createFlightService,
    FlightChangeCase,
    IFlightService,
    CaseStatus,
    isCaseReadOnlyStatus,
    mapCaseStatusesToApiCodes,
    SortByField,
    SortOrder,
} from '../../services/api/flightChanges';
import { AUTO_REFRESH_CONFIG } from '../../services/api/flightChanges/constants';
import { isNotFoundError, isUnauthorizedError, getErrorMessage } from '../../services/api/flightChanges/errors';
import { useAuthStore } from '../../stores/authStore';
import {
    DEFAULT_FLIGHT_CHANGE_CASES_COUNT,
    navigateFlightChangeCasesSearch,
    parseFlightChangeCasesSearch,
    type FlightChangeCasesListSearchState,
} from './flightChangeCasesSearchParams';

const AWB_MIN_LENGTH_FOR_SEARCH = 4;

/** API status param values (CSV) mapped to CaseStatus for filter UI */
const API_CODE_TO_CASE_STATUS: Record<string, CaseStatus> = {
    PENDING: CaseStatus.PENDING,
    ON_HOLD: CaseStatus.ON_HOLD,
    IN_ANALYSIS: CaseStatus.IN_ANALYSIS,
};

function statusCodesToSelectedFilters(codes: string[]): CaseStatus[] {
    return codes.map((c) => API_CODE_TO_CASE_STATUS[c.trim()]).filter((s): s is CaseStatus => s != null);
}

export const useFlightCasesService = (): IFlightService => {
    const apiService = useAuthStore((state) => state.apiService);
    const isInitialized = useAuthStore((state) => state.isInitialized);
    
    return React.useMemo(() => {
        if (!apiService || !isInitialized) {
            return {
                getFlights: async () => { throw new Error('API service not initialized'); },
                getAnalysts: async () => { throw new Error('API service not initialized'); },
                getFlightCaseDetailById: async () => { throw new Error('API service not initialized'); },
                getReasonCodes: async () => { throw new Error('API service not initialized'); },
                updateFlightCase: async () => { throw new Error('API service not initialized'); },
                assignFlightCase: async () => { throw new Error('API service not initialized'); },
            } as IFlightService;
        }
        return createFlightService(apiService);
    }, [apiService, isInitialized]);
};

export const useFlightChangeCases = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Parse the first-seen query string only so `useState` initializers match the first paint.
    // Ongoing URL ↔ state sync is handled by the `useEffect` on `searchParams` (browser back/forward, external edits).
    const parsedOnMount = React.useMemo(
        () => parseFlightChangeCasesSearch(searchParams.toString()),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: see comment above
        [],
    );

    const [flightsCases, setFlightsCases] = React.useState<FlightChangeCase[]>([]);
    const [total, setTotal] = React.useState<number>(0);
    const [page, setPage] = React.useState(parsedOnMount.page);
    const [pageSize, setPageSize] = React.useState(parsedOnMount.count);
    const [sortBy, setSortBy] = React.useState<SortByField>(parsedOnMount.sortBy);
    const [sortOrder, setSortOrder] = React.useState<SortOrder>(parsedOnMount.sortOrder);
    const [selectedStatusFilters, setSelectedStatusFilters] = React.useState<CaseStatus[]>(
        statusCodesToSelectedFilters(parsedOnMount.statusCodes),
    );
    const [awbFilterInput, setAwbFilterInput] = React.useState(parsedOnMount.awbInput);
    const [selectedAnalystId, setSelectedAnalystId] = React.useState<number | null>(parsedOnMount.analystId);
    const [filterUnassigned, setFilterUnassigned] = React.useState(parsedOnMount.filterUnassigned);
    const [analystOptions, setAnalystOptions] = React.useState<AnalystOption[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [isSorting, setIsSorting] = React.useState<boolean>(false);
    const [hasCompletedInitialFetch, setHasCompletedInitialFetch] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    const flightCaseService = useFlightCasesService();
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const isInitialized = useAuthStore((state) => state.isInitialized);
    const account = useAuthStore((state) => state.account);

    const awbQueryParam = React.useMemo(() => {
        const trimmed = awbFilterInput.trim();
        return trimmed.length >= AWB_MIN_LENGTH_FOR_SEARCH ? trimmed : undefined;
    }, [awbFilterInput]);

    const pushUrlState = React.useCallback(
        (
            overrides: Partial<FlightChangeCasesListSearchState> = {},
            options?: { replace?: boolean },
        ) => {
            const next: FlightChangeCasesListSearchState = {
                sortBy: overrides.sortBy ?? sortBy,
                sortOrder: overrides.sortOrder ?? sortOrder,
                page: overrides.page ?? page,
                count: overrides.count ?? pageSize,
                statusCodes:
                    overrides.statusCodes !== undefined
                        ? overrides.statusCodes
                        : mapCaseStatusesToApiCodes(selectedStatusFilters),
                awbInput: overrides.awbInput !== undefined ? overrides.awbInput : awbFilterInput,
                analystId: 'analystId' in overrides ? overrides.analystId! : selectedAnalystId,
                filterUnassigned:
                    'filterUnassigned' in overrides
                        ? (overrides.filterUnassigned ?? false)
                        : filterUnassigned,
            };
            navigateFlightChangeCasesSearch(navigate, location.pathname, location.search, next, options);
        },
        [
            navigate,
            location.pathname,
            location.search,
            sortBy,
            sortOrder,
            page,
            pageSize,
            selectedStatusFilters,
            awbFilterInput,
            selectedAnalystId,
            filterUnassigned,
        ],
    );

    React.useEffect(() => {
        const parsed = parseFlightChangeCasesSearch(searchParams.toString());
        setPage((p) => (p !== parsed.page ? parsed.page : p));
        setPageSize((c) => (c !== parsed.count ? parsed.count : c));
        setSortBy((s) => (s !== parsed.sortBy ? parsed.sortBy : s));
        setSortOrder((o) => (o !== parsed.sortOrder ? parsed.sortOrder : o));
        setSelectedStatusFilters((prev) => {
            const next = statusCodesToSelectedFilters(parsed.statusCodes);
            if (prev.length !== next.length) {
                return next;
            }
            return prev.every((status, i) => status === next[i]) ? prev : next;
        });
        setAwbFilterInput((prev) => (prev !== parsed.awbInput ? parsed.awbInput : prev));
        setSelectedAnalystId((prev) => (prev !== parsed.analystId ? parsed.analystId : prev));
        setFilterUnassigned((prev) => (prev !== parsed.filterUnassigned ? parsed.filterUnassigned : prev));
    }, [searchParams]);

    const updateCountRef = React.useRef<number>(0);
    const intervalIdRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
    const timeoutIdRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const hasInitialDataLoadedRef = React.useRef<boolean>(false);
    const isIntervalConfiguredRef = React.useRef<boolean>(false);
    const isFetchingRef = React.useRef<boolean>(false);

    const fetchFlightsCases = React.useCallback(async (isAutoRefresh: boolean = false) => {
        if (!isInitialized) {
            return;
        }

        if (!isAuthenticated || !account) {
            if (!isAutoRefresh) {
                setLoading(false);
            }
            return;
        }

        try {
            if (!isAutoRefresh) {
                setLoading(true);
            }
            setError(null);
            const statusCodes = mapCaseStatusesToApiCodes(selectedStatusFilters);
            const result = await flightCaseService.getFlights({
                page,
                count: pageSize,
                sortBy,
                sortOrder,
                ...(statusCodes.length > 0 ? { statusCodes } : {}),
                ...(awbQueryParam != null ? { awb: awbQueryParam } : {}),
                ...(filterUnassigned
                    ? { isUnassigned: true }
                    : selectedAnalystId != null
                      ? { analystId: selectedAnalystId }
                      : {}),
            });
            setFlightsCases(result.data);
            setTotal(result.total);
            hasInitialDataLoadedRef.current = true;
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            
            if (isUnauthorizedError(err) || errorMessage.includes('No active session')) {
                console.warn('Token expired or invalid, or no active session.');
                return;
            }
            
            if (isNotFoundError(err)) {
                setError('NOT_FOUND');
            } else {
                setError(errorMessage);
            }
        } finally {
            if (!isAutoRefresh) {
                setLoading(false);
                setIsSorting(false);
                setHasCompletedInitialFetch(true);
            }
        }
    }, [
        flightCaseService,
        isInitialized,
        isAuthenticated,
        account,
        page,
        pageSize,
        sortBy,
        sortOrder,
        selectedStatusFilters,
        awbQueryParam,
        selectedAnalystId,
        filterUnassigned,
    ]);

    React.useEffect(() => {
        if (!isInitialized || !isAuthenticated || !account) {
            return;
        }
        let cancelled = false;
        flightCaseService
            .getAnalysts()
            .then((list) => {
                if (!cancelled) {
                    setAnalystOptions(Array.isArray(list) ? list : []);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setAnalystOptions([]);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [flightCaseService, isInitialized, isAuthenticated, account]);

    React.useEffect(() => {
        if (isInitialized && isAuthenticated && account) {
            fetchFlightsCases();
        } else if (isInitialized && !isAuthenticated) {
            setLoading(false);
        }
    }, [fetchFlightsCases, isInitialized, isAuthenticated, account]);

    React.useEffect(() => {
        if (!loading && hasInitialDataLoadedRef.current && !isIntervalConfiguredRef.current && 
            isAuthenticated && account) {
            isIntervalConfiguredRef.current = true;
            const INTERVAL_TIME = AUTO_REFRESH_CONFIG.INTERVAL_TIME_MS;
            const MAX_UPDATES = AUTO_REFRESH_CONFIG.MAX_UPDATES; 

            timeoutIdRef.current = setTimeout(() => {
                fetchFlightsCases(true);
                updateCountRef.current = 1;

                intervalIdRef.current = setInterval(() => {
                    if (updateCountRef.current < MAX_UPDATES && !isFetchingRef.current) {
                        isFetchingRef.current = true;

                        fetchFlightsCases(true).finally(() => {
                            isFetchingRef.current = false;
                        });

                        updateCountRef.current += 1;
                    } else {
                        if (intervalIdRef.current) {
                            clearInterval(intervalIdRef.current);
                            intervalIdRef.current = null;
                        }
                    }
                }, INTERVAL_TIME);
            }, INTERVAL_TIME);
        }

        return () => {
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
                timeoutIdRef.current = null;
            }
            if (intervalIdRef.current) {
                clearInterval(intervalIdRef.current);
                intervalIdRef.current = null;
            }

            updateCountRef.current = 0;
            isIntervalConfiguredRef.current = false;
            isFetchingRef.current = false;
        };
    }, [loading, fetchFlightsCases, isAuthenticated, account]);

    const refetch = React.useCallback(async () => {
        await fetchFlightsCases(true);
    }, [fetchFlightsCases]);

    const updateFlightAnalyst = React.useCallback((flightChangeId: number, analyst: { id: number; name: string; email: string }) => {
        setFlightsCases(prevFlights => 
            prevFlights.map(flight => 
                flight.flightChangeId === flightChangeId 
                    ? { ...flight, analyst, status: CaseStatus.IN_ANALYSIS }
                    : flight
            )
        );
    }, []);

    const visibleFlights = React.useMemo(() => 
        flightsCases.filter(flight => flight.status !== CaseStatus.TO_NOTIFY),
        [flightsCases]
    );

    const handlePageChange = React.useCallback(
        (newPage: number) => {
            setPage(newPage);
            pushUrlState({ page: newPage });
        },
        [pushUrlState],
    );

    const handlePageSizeChange = React.useCallback(
        (newPageSize: number) => {
            const resolvedSize = Number.isNaN(newPageSize) ? DEFAULT_FLIGHT_CHANGE_CASES_COUNT : newPageSize;
            const totalPages = Math.ceil(total / resolvedSize) || 1;
            const nextPage = Math.min(page, Math.max(0, totalPages - 1));
            setPageSize(resolvedSize);
            setPage(nextPage);
            pushUrlState({ page: nextPage, count: resolvedSize });
        },
        [total, page, pushUrlState],
    );

    const handleStatusFilterChange = React.useCallback(
        (next: CaseStatus[]) => {
            setSelectedStatusFilters(next);
            setPage(0);
            pushUrlState({ page: 0, statusCodes: mapCaseStatusesToApiCodes(next) });
        },
        [pushUrlState],
    );

    const handleAwbFilterChange = React.useCallback(
        (next: string) => {
            setAwbFilterInput(next);
            setPage(0);
            pushUrlState({ page: 0, awbInput: next }, { replace: true });
        },
        [pushUrlState],
    );

    const handleAnalystFilterChange = React.useCallback(
        (selection: { analystId: number | null; filterUnassigned: boolean }) => {
            const { analystId, filterUnassigned } = selection;
            setSelectedAnalystId(analystId);
            setFilterUnassigned(filterUnassigned);
            setPage(0);
            pushUrlState({ page: 0, analystId, filterUnassigned });
        },
        [pushUrlState],
    );

    const handleRequestSort = React.useCallback(
        (property: SortByField) => {
            setIsSorting(true);
            if (sortBy === property) {
                const nextOrder: SortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
                setSortOrder(nextOrder);
                setPage(0);
                pushUrlState({
                    sortBy: property,
                    sortOrder: nextOrder,
                    page: 0,
                });
            } else {
                setSortBy(property);
                setSortOrder('desc');
                setPage(0);
                pushUrlState({
                    sortBy: property,
                    sortOrder: 'desc',
                    page: 0,
                });
            }
        },
        [sortBy, sortOrder, pushUrlState],
    );

    const hasActiveFilters = React.useMemo(() => {
        const awbAppliesToQuery = awbFilterInput.trim().length >= AWB_MIN_LENGTH_FOR_SEARCH;
        return (
            awbAppliesToQuery ||
            selectedStatusFilters.length > 0 ||
            selectedAnalystId != null ||
            filterUnassigned
        );
    }, [awbFilterInput, selectedStatusFilters, selectedAnalystId, filterUnassigned]);

    const clearAllFilters = React.useCallback(() => {
        setSelectedStatusFilters([]);
        setAwbFilterInput('');
        setSelectedAnalystId(null);
        setFilterUnassigned(false);
        setPage(0);
        pushUrlState({
            page: 0,
            statusCodes: [],
            awbInput: '',
            analystId: null,
            filterUnassigned: false,
        });
    }, [pushUrlState]);

    return {
        flights: visibleFlights,
        loading,
        hasCompletedInitialFetch,
        error,
        refetch,
        updateFlightAnalyst,
        total,
        page,
        pageSize,
        setPage: handlePageChange,
        setPageSize: handlePageSizeChange,
        sortBy,
        sortOrder,
        handleRequestSort,
        isSorting,
        isCaseReadOnly: isCaseReadOnlyStatus,
        selectedStatusFilters,
        onStatusFilterChange: handleStatusFilterChange,
        awbFilterInput,
        onAwbFilterChange: handleAwbFilterChange,
        selectedAnalystId,
        filterUnassigned,
        analystOptions,
        onAnalystFilterChange: handleAnalystFilterChange,
        hasActiveFilters,
        clearAllFilters,
    };
};
